import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { Menu } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { enableAdvancedSetting } from '../EnableAdvancedSetting';
import { useGetSettings } from '../hooks/useGetSettings/useGetSettings';
import { AdvancedSettingCategory, ADVANCED_SETTING_CATEGORY_MAP } from '../ServiceAdvancedSettings.types';
import { isSettingActive } from '../ServiceAdvancedSettings.utils';
export const GetServiceAdvancedSettingsPopoverTID = (parentTestId)=>getTestIds(parentTestId);
export function ServiceAdvancedSettingsPopover(props) {
    const { t: texts } = useLocaleKeys('components.serviceAdvancedSettings');
    const { getNow } = useIoContext();
    const now = useMemo(()=>getNow(), [
        getNow
    ]);
    const effectiveDate = props.relationshipState.effectiveDate;
    const isApprovedRelationship = props.relationshipState.type === 'approved';
    const isClosingRef = useRef(false);
    const testIds = useMemo(()=>GetServiceAdvancedSettingsPopoverTID(props.testId), [
        props.testId
    ]);
    const { ServiceAdvancedSettingsPopoverTestIds } = testIds;
    const settings = useGetSettings({
        isFreshlyAdded: props.isFreshlyAdded,
        isApprovedRelationship,
        service: props.service,
        now,
        effectiveDate
    });
    const activeSettingsFromService = useMemo(()=>{
        return new Set(settings.filter((setting)=>isSettingActive({
                type: setting.type,
                service: props.service
            })).map((setting)=>setting.type));
    }, [
        props.service,
        settings
    ]);
    const settingsByCategory = useMemo(()=>{
        const grouped = {
            [AdvancedSettingCategory.Pricing]: [],
            [AdvancedSettingCategory.Billing]: [],
            [AdvancedSettingCategory.Integrations]: []
        };
        settings.forEach((setting)=>{
            const category = ADVANCED_SETTING_CATEGORY_MAP[setting.type];
            grouped[category].push(setting);
        });
        return grouped;
    }, [
        settings
    ]);
    const categories = useMemo(()=>[
            AdvancedSettingCategory.Pricing,
            AdvancedSettingCategory.Billing,
            AdvancedSettingCategory.Integrations
        ], []);
    const categoryLabels = {
        [AdvancedSettingCategory.Pricing]: texts.categories.pricing(),
        [AdvancedSettingCategory.Billing]: texts.categories.billing(),
        [AdvancedSettingCategory.Integrations]: texts.categories.integrations()
    };
    const addSetting = useCallback((type)=>{
        isClosingRef.current = true;
        props.onClose();
        enableAdvancedSetting(type, {
            service: props.service,
            now,
            relationshipState: props.relationshipState
        }, props);
    }, [
        now,
        props
    ]);
    const menuItems = useMemo(()=>{
        const items = [];
        categories.forEach((category)=>{
            const categorySettings = settingsByCategory[category];
            if (categorySettings.length === 0) return;
            items.push({
                id: ServiceAdvancedSettingsPopoverTestIds.Category(category),
                testId: ServiceAdvancedSettingsPopoverTestIds.Category(category),
                isSectionHeader: true,
                label: categoryLabels[category]
            });
            categorySettings.forEach((setting)=>{
                var _ref;
                const isAlreadyAdded = activeSettingsFromService.has(setting.type);
                items.push({
                    id: ServiceAdvancedSettingsPopoverTestIds.Item(category, setting.type),
                    testId: ServiceAdvancedSettingsPopoverTestIds.Item(category, setting.type),
                    disabled: isAlreadyAdded || setting.disabled,
                    tooltip: !isClosingRef.current && isAlreadyAdded ? texts.alreadyAddedTooltip() : (_ref = setting.disabled && setting.disabledTooltip) !== null && _ref !== void 0 ? _ref : '',
                    body: /*#__PURE__*/ _jsx("span", {
                        children: setting.optionName
                    }),
                    onClick: isAlreadyAdded || setting.disabled ? undefined : ()=>{
                        addSetting(setting.type);
                    },
                    searchText: setting.label
                });
            });
        });
        return items;
    }, [
        activeSettingsFromService,
        addSetting,
        categories,
        categoryLabels,
        ServiceAdvancedSettingsPopoverTestIds,
        settingsByCategory,
        texts
    ]);
    return /*#__PURE__*/ _jsx(Menu, {
        testId: ServiceAdvancedSettingsPopoverTestIds.Menu(),
        items: menuItems,
        emptyText: texts.noMatchesEmpty(),
        dimensions: {
            height: 'fit-content',
            maxHeight: 344,
            width: 344
        },
        searchProps: {
            bi: {
                name: 'advanced_settings_search'
            },
            showSearch: true,
            autoFocus: true,
            placeholder: texts.searchPlaceholder(),
            strategy: {
                type: 'custom',
                filterItem: (search, item)=>{
                    var _item_searchText;
                    const searchText = (_item_searchText = item.searchText) !== null && _item_searchText !== void 0 ? _item_searchText : '';
                    return searchText.toLowerCase().includes(search.trim().toLowerCase());
                }
            }
        }
    });
}
